import { jsPDF } from 'jspdf';

// ============================================================================
// INTEGRATION DOC — PDF generator
// Hand-out document for webhook based lead sources (MagicBricks, Website).
// Contains the endpoint, the generated API key, the accepted fields and a
// ready to paste cURL, so the portal/website team can wire it up without help.
// ============================================================================

const NAVY = [22, 43, 77];
const SLATE = [100, 116, 139];
const INK = [30, 41, 59];
const LINE = [226, 232, 240];
const SOFT = [245, 247, 250];

const FIELD_ROWS = {
  magicbricks: [
    ['name', 'Lead full name'],
    ['phone / mobile', 'Contact number (phone or email required)'],
    ['email', 'Email address'],
    ['country_code', 'Defaults to 91'],
    ['lead_id / query_id', 'Portal lead id — used to skip duplicates'],
    ['project_name', 'Project the enquiry belongs to'],
    ['city, locality', 'Location of the enquiry'],
    ['message', 'Enquiry text'],
    ['date', 'Lead date (ISO). Defaults to received time'],
  ],
  website: [
    ['name', 'Lead full name (or first_name + last_name)'],
    ['phone / mobile', 'Contact number (phone or email required)'],
    ['email', 'Email address'],
    ['country_code', 'Defaults to 91'],
    ['project_name / project', 'Project the enquiry belongs to'],
    ['city, locality', 'Location of the enquiry'],
    ['property_type', 'Apartment, Villa, Plot, etc.'],
    ['configuration / bhk', 'Unit configuration'],
    ['min_budget, max_budget', 'Budget range (numbers)'],
    ['service_type', 'Buy / Rent — defaults to Buy'],
    ['form_name', 'Which form on the site submitted this lead'],
    ['message', 'Enquiry text'],
  ],
};

const SAMPLE_PAYLOAD = {
  magicbricks: {
    lead_id: 'MB123456',
    name: 'Rahul Sharma',
    phone: '9876543210',
    email: 'rahul@example.com',
    project_name: 'Skyline Heights',
    city: 'Mumbai',
    locality: 'Dahisar',
    message: 'Interested in 2 BHK',
  },
  website: {
    name: 'Rahul Sharma',
    phone: '9876543210',
    email: 'rahul@example.com',
    project_name: 'Skyline Heights',
    city: 'Mumbai',
    configuration: '2 BHK',
    max_budget: 8500000,
    service_type: 'Buy',
    form_name: 'Contact Us',
  },
};

const fileSlug = (val) =>
  String(val || 'source').trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').slice(0, 40) || 'source';

export const buildSampleCurl = ({ webhookUrl, apiKey, sourceId }) => {
  const payload = JSON.stringify(SAMPLE_PAYLOAD[sourceId] || SAMPLE_PAYLOAD.website);
  return `curl -X POST "${webhookUrl}" -H "Content-Type: application/json" -H "X-API-Key: ${apiKey}" -d '${payload}'`;
};

export const downloadIntegrationDoc = ({
  sourceId,
  sourceName,
  clientCode,
  branch,
  apiKey,
  webhookUrl,
  websiteName,
}) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const PAGE_W = 210;
  const PAGE_H = 297;
  const M = 16;
  const contentW = PAGE_W - 2 * M;
  let y = 0;

  const ensureSpace = (h) => {
    if (y + h > PAGE_H - 18) {
      pdf.addPage();
      y = M + 6;
    }
  };

  const sectionTitle = (text) => {
    ensureSpace(14);
    y += 4;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...NAVY);
    pdf.text(text.toUpperCase(), M, y);
    y += 2.5;
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.3);
    pdf.line(M, y, M + contentW, y);
    y += 6;
  };

  const keyValue = (label, value) => {
    ensureSpace(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...SLATE);
    pdf.text(label, M, y);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...INK);
    pdf.text(String(value || '—'), M + 46, y);
    y += 6.5;
  };

  const codeBlock = (text, mono = true) => {
    pdf.setFont(mono ? 'courier' : 'helvetica', 'normal');
    pdf.setFontSize(8.5);
    const lines = pdf.splitTextToSize(text, contentW - 8);
    const boxH = lines.length * 4.6 + 7;
    ensureSpace(boxH + 3);
    pdf.setFillColor(...SOFT);
    pdf.setDrawColor(...LINE);
    pdf.roundedRect(M, y, contentW, boxH, 2, 2, 'FD');
    pdf.setTextColor(...INK);
    pdf.text(lines, M + 4, y + 6);
    y += boxH + 4;
  };

  // ── Header ──
  pdf.setFillColor(...NAVY);
  pdf.rect(0, 0, PAGE_W, 34, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(17);
  pdf.setTextColor(255, 255, 255);
  pdf.text('LeaDBoat', M, 15);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(203, 213, 225);
  pdf.text('Lead Source Integration Guide', M, 22);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(255, 255, 255);
  pdf.text(sourceName || 'Integration', PAGE_W - M, 15, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(203, 213, 225);
  pdf.text(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    PAGE_W - M, 22, { align: 'right' });
  y = 46;

  // ── Account ──
  sectionTitle('Account Details');
  keyValue('Source', sourceName);
  keyValue('Client Code', clientCode);
  keyValue('Branch', branch);
  if (websiteName) keyValue('Website', websiteName);

  // ── Endpoint ──
  sectionTitle('Endpoint');
  keyValue('Method', 'POST (GET with query params also accepted)');
  keyValue('Content-Type', 'application/json');
  codeBlock(webhookUrl || '');

  // ── Auth ──
  sectionTitle('Authentication');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...SLATE);
  pdf.text('Send this key on every request in the X-API-Key header. Keep it private.', M, y);
  y += 6;
  codeBlock(`X-API-Key: ${apiKey || ''}`);

  // ── Fields ──
  sectionTitle('Accepted Fields');
  const rows = FIELD_ROWS[sourceId] || FIELD_ROWS.website;
  rows.forEach(([field, desc], i) => {
    ensureSpace(7);
    if (i % 2 === 0) {
      pdf.setFillColor(250, 251, 253);
      pdf.rect(M, y - 4, contentW, 6.5, 'F');
    }
    pdf.setFont('courier', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...INK);
    pdf.text(field, M + 2, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...SLATE);
    pdf.text(desc, M + 58, y);
    y += 6.5;
  });
  y += 2;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...SLATE);
  pdf.text('At least one of phone or email is required. Duplicate leads are ignored automatically.', M, y);
  y += 6;

  // ── Sample ──
  sectionTitle('Sample Request');
  codeBlock(JSON.stringify(SAMPLE_PAYLOAD[sourceId] || SAMPLE_PAYLOAD.website, null, 2));
  codeBlock(buildSampleCurl({ webhookUrl, apiKey, sourceId }));

  // ── Response ──
  sectionTitle('Response');
  codeBlock('{ "success": true, "message": "Lead received successfully", "lead_id": 1234 }');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...SLATE);
  const notes = pdf.splitTextToSize(
    'A 401 means the API key is wrong, 404 means the integration is not active for this client code, '
    + 'and 400 means the lead had neither a phone nor an email. Leads land in the CRM within seconds of a successful call.',
    contentW
  );
  ensureSpace(notes.length * 4.6);
  pdf.text(notes, M, y);

  // ── Footer on every page ──
  const pages = pdf.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    pdf.setPage(i);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...SLATE);
    pdf.text('Generated by LeaDBoat CRM — share this document with your lead source provider.', M, PAGE_H - 10);
    pdf.text(`${i} / ${pages}`, PAGE_W - M, PAGE_H - 10, { align: 'right' });
  }

  pdf.save(`${fileSlug(sourceName)}_Integration_${fileSlug(clientCode)}_${fileSlug(branch)}.pdf`);
};
